import {
  Stack,
  useBreakpointValue,
  Box,
  Spinner,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState, useCallback, useRef, useMemo } from "react";
import actions from "../actions";
import { KYDAdminEventDetail } from "@common/interfaces/KYDEvent";
import { Outlet, useParams, useLocation, matchPath } from "react-router-dom";
import { ModalType, EditModalType } from "../utils";
import "react-datepicker/dist/react-datepicker.css";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import customParseFormat from "dayjs/plugin/customParseFormat";
import relativeTime from "dayjs/plugin/relativeTime";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useDialog } from "../Common/Dialog";
import { useLoading } from "./hooks/form";
import {
  DesktopSideBar,
  MobileNavBar,
} from "./EventDetail/Nav/EventDetailsNavigation";

dayjs.extend(timezone);
dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);

const EventDetail = ({
  setAppModalType,
}: {
  setAppModalType: (state: ModalType, description: string) => void;
}) => {
  const [kydEvent, setKydEvent] = useState<KYDAdminEventDetail | null>(null);
  const { event_id } = useParams();
  const [loading, updateLoading] = useLoading({
    tickettype: false,
    promocode: false,
    create_affiliates: false,
    blocking: false,
    event: false,
    metrics: false,
  });

  const isMobile = useBreakpointValue({
    base: true,
    md: false,
  });

  const dialog = useDialog();

  const fetchEvent = useCallback(
    async (eventId?: string, options?: any) => {
      if (!options || (options && !options.silent)) {
        updateLoading("init", true);
      }

      let _;
      try {
        if (!eventId) {
          throw Error("NO event ID provided");
        }
        _ = await actions.ADMIN.fetchEvent(eventId);
        setKydEvent(_);
        dayjs.tz.setDefault(_.timezone);
      } catch (err) {
        dialog({
          text: err.message,
          actions: [
            {
              type: "confirm",
              text: "Got it",
              onClick: () => {
                window.location.assign("/");
              },
            },
          ],
        });
      }
      updateLoading("init", false);
      return _;
    },
    [updateLoading]
  );

  useEffect(() => {
    if (event_id) {
      setKydEvent(null);
      fetchEvent(event_id);
    }
  }, [event_id, fetchEvent]);

  const portalRef = useRef<HTMLDivElement>(null);
  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      {isMobile && (
        <Box
          top={0}
          left={0}
          right={0}
          bottom={0}
          h="full"
          w="full"
          position="absolute"
          ref={portalRef}
        ></Box>
      )}
      <Stack
        spacing={0}
        padding={0}
        direction={["column", "row"]}
        width={"full"}
        h="full"
        overflow={isMobile ? "auto" : "hidden"}
        // minH="100%"
        flexGrow={1}
        position="relative"
      >
        {!isMobile && (
          <Box
            top={0}
            left={0}
            right={0}
            bottom={0}
            h="full"
            w="full"
            position="absolute"
            ref={portalRef}
          ></Box>
        )}
        <DesktopSideBar
          show={!isMobile}
          nav={kydEvent?.nav}
          isLoading={loading.event}
        />
        <Stack
          spacing={{ base: "8", lg: "6" }}
          transform="translate3d(0,0,0)"
          width="100%"
          // @ts-ignore
          style={{ "::-webkit-scrollbar": { display: "none" } }}
          // height="100%"
        >
          {loading["init"] && (
            <VStack padding={8}>
              <Spinner />
            </VStack>
          )}

          {kydEvent && (
            <Stack
              spacing={10}
              minWidth={["100%", "initial"]}
              width="100%"
              overflow="auto"
              p={[4, 8]}
              display="flex"
              alignItems="center"
              justifyContent="flex-start"
            >
              <MobileNavBar
                show={isMobile}
                nav={kydEvent?.nav}
                isLoading={loading.event}
              />
              <Outlet
                context={{ portalRef }}
                key={event_id}
              />
            </Stack>
          )}
        </Stack>
      </Stack>
    </LocalizationProvider>
  );
};

export default EventDetail;
